<template>
  <div>
    <section-title
      content="1. Identificación del Estudiante"
      class="subsection-title"
      icon="fas fa-info-circle"
      reverse
    />
    <div class="student-identification">
      <section-title
        content="Datos del Estudiante"
        has-line
        class="subsection-title"
      />
      <div class="info-grid">
        <display-field label="Nombre del Estudiante:" :value="student.name" />
        <display-field label="Número de SIE:" :value="student.sie" />
        <display-field label="Núm. Seguro Social:" :value="student.ssn" />
        <display-field label="Edad Cronológica:" :value="student.age" />
        <display-field label="Género:" :value="student.gender" />
        <display-field
          label="Lugar de Nacimiento:"
          :value="student.birthPlace"
        />
        <display-field
          label="Fecha de Nacimiento:"
          :value="formattedBirthdate"
        />
        <display-field label="Correo Electrónico:" :value="student.email" />
        <display-field label="Teléfono:" :value="formattedStudentPhone" />
        <div class="date-field">
          <validation-provider
            v-slot="{ errors }"
            name="Fecha de Referido"
            rules="required"
          >
            <custom-date-picker
              v-model="model.referralDate"
              format="dd/MMM./yyyy"
              label="Fecha de Referido:"
              placeholder="DD/MM/YYYY"
              :disabled="readOnly"
              :min-date="today"
              class-container=""
              :error="errors[0]"
            />
            <error v-if="errors[0]" variant="no-border" :message="errors[0]" />
          </validation-provider>
        </div>
      </div>

      <section-title
        content="Datos de Escuela"
        has-line
        class="subsection-title"
      />
      <div class="info-grid">
        <display-field label="Escuela:" :value="school.school" />
        <display-field
          label="Dirección (línea 1):"
          :value="school.addressLine1"
        />
        <display-field
          label="Dirección (línea 2):"
          :value="school.addressLine2"
        />
        <display-field label="País:" :value="school.country" />
        <display-field label="Estado:" :value="school.state" />
        <display-field label="Municipio:" :value="school.municipality" />
        <display-field label="Código postal:" :value="school.zipCode" />
        <display-field
          label="Nombre del Director:"
          :value="school.directorName"
        />
      </div>

      <section-title
        content="Datos de Dirección"
        has-line
        class="subsection-title"
      />
      <div class="info-grid">
        <display-field
          label="Dirección (línea 1):"
          :value="address.addressLine1"
        />
        <display-field
          label="Dirección (línea 2):"
          :value="address.addressLine2"
        />
        <display-field label="País:" :value="address.country" />
        <display-field label="Estado:" :value="address.state" />
        <display-field label="Municipio:" :value="address.municipality" />
        <display-field label="Código postal:" :value="address.zipCode" />
      </div>

      <section-title
        content="Contacto de Emergencia"
        has-line
        class="subsection-title"
      />
      <div class="info-grid">
        <display-field label="Nombre:" :value="emergencyContact.name" />
        <display-field
          label="Parentesco:"
          :value="emergencyContact.relationship"
        />
        <display-field label="Teléfono:" :value="formattedEmergencyPhone" />
        <display-field
          label="Correo Electrónico:"
          :value="emergencyContact.email"
        />
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomDatePicker from 'custom-date-picker';
import Error from '@/components/common/Error';
import { date, phone } from '@/utils/filters';

const DisplayField = {
  name: 'DisplayField',
  functional: true,
  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number],
      default: '-',
    },
  },
  render(h, { props }) {
    return h('div', { class: 'display-field' }, [
      h('p', { class: 'field-label' }, props.label),
      h('p', { class: 'field-value' }, props.value || '-'),
    ]);
  },
};

export default {
  name: 'StudentIdentificationSection',
  components: {
    SectionTitle,
    CustomDatePicker,
    DisplayField,
    Error,
  },
  props: {
    readOnly: Boolean,
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    address() {
      return this.model.addressInfo || {};
    },
    emergencyContact() {
      return this.model.emergencyContactInfo || {};
    },
    formattedBirthdate() {
      return date(this.student.birthdate);
    },
    formattedEmergencyPhone() {
      return phone(this.emergencyContact.phone);
    },
    formattedStudentPhone() {
      return phone(this.student.phone);
    },
    model: {
      get() {
        return this.value || {};
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    school() {
      return this.model.schoolInfo || {};
    },
    student() {
      return this.model.studentIdentity || {};
    },
    today() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
  },
};
</script>

<style lang="scss" scoped>
.student-identification {
  background: #fdfdfb;
  padding: 18px 18px 8px;
}

.step-header {
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
}

.step-icon {
  align-items: center;
  background: #ff6d5f;
  border-radius: 50%;
  color: #fdfdfb;
  display: inline-flex;
  flex: 0 0 16px;
  font-size: 8px;
  height: 16px;
  justify-content: center;
  width: 16px;
}

.step-title {
  background: #e9e9e9;
  border-radius: 14px;
  color: #222f2c;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  min-width: 260px;
  padding: 7px 18px;
}

.subsection-title {
  margin-bottom: 14px;
  margin-top: 14px;
}

.info-grid {
  display: grid;
  gap: 14px 88px;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  margin-bottom: 10px;
}

.display-field {
  min-width: 0;
}

.field-label {
  color: #6d7472;
  font-size: 12px;
  line-height: 1.25;
  margin-bottom: 6px;
}

.field-value {
  color: #17342f;
  font-size: 14px;
  line-height: 1.35;
  margin-bottom: 0;
  word-break: break-word;
}

.date-field {
  max-width: 280px;
}

::v-deep .subsection-title {
  .section-title,
  .title {
    font-size: 12px;
  }
}

@media (max-width: 991.98px) {
  .info-grid {
    gap: 14px 28px;
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }
}

@media (max-width: 575.98px) {
  .student-identification {
    padding: 12px 0 4px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .step-title {
    min-width: 0;
    width: 100%;
  }

  .date-field {
    max-width: none;
  }
}
</style>
